import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { stringify } from "query-string";
import { store } from "~/store";
import { updateToken } from "~/store/auth/actions";
import history from "~/packages/history";
import ROUTES from "~/constants/routes";
import ENV from "~/constants";
import { httpError } from "./common";
import { msalInstance, loginRequest } from "~/packages/msalInstance";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import Auth from "./auth";
import Permissions from "./permissions";
import Profile from "./profile";
import Roles from "./roles";
import Sites from "./sites";
import Poses from "./poses";
import Bundles from "./bundles";
import Search from "./search";
import Services from "./services";
import EvPorts from "./evports";
import Users from "./users";
import Incidents from "./incidents";
import Groups from "./groups";
import PaymentServices from "./paymentServices";
import DashboardSettings from "./dashboardSettings";
import UserActivityJournal from "./userActivityJournal";
import EventsLogJournal from "./eventsLogJournal";
import SystemLogJournal from "./systemLogJournal";
import JournalBusinessOperations from "./journalBusinessOperations";
import SystemSettings from "./systemSettings";
import Metrics from "./metrics";
import DashboardMetrics from "./dashboardMetrics";
import DashboardCatalogs from "./dashboardCatalogs";
import ServicesCategories from "./servicesCategories";
import ServiceTemplates from "./serviceTemplates";
import ZReportsJournal from "./zReportsJournal";
import Goods from "./goods";
import KeyManagement from "./keyManagement";
import idPrefix from "./id-prefix";

const serverConfig = {
  baseURL: ENV.REACT_APP_API_URL,
  headers: { "Access-Control-Allow-Origin": "*" },
  paramsSerializer: {
    serialize: (params: Record<string, any>) => stringify(params),
  },
};

const serverInstance = axios.create(serverConfig);

// ----- Request interceptor -----
const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const state: ReduxStore.State = store.getState();
  const { authData } = state.auth;
  if (authData?.accessToken) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${authData.accessToken}`;
  }
  return config;
};

// ----- Refresh concurrency guard -----
let refreshPromise: Promise<string> | null = null;

// ----- Response error interceptor (async/await) -----
const responseErrorInterceptor = async (httpResponse: {
  response: AxiosResponse<Api.ErrorResponse>;
}) => {
  const { response } = httpResponse;
  if (!response) return Promise.reject(httpResponse);

  const originalConfig = response.config as InternalAxiosRequestConfig & { _retry?: boolean };

  // Handle 401 once (skip if already retried or is refresh call)
  if (
    response.status === 401 &&
    !originalConfig._retry &&
    !originalConfig.url?.includes("/auth/refresh")
  ) {
    const state: ReduxStore.State = store.getState();
    const refreshToken = state.auth.authData?.refreshToken;

    if (!refreshToken) {
      return Promise.reject(response);
    }

    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          // primary attempt: backend refresh
          const { data } = await api.auth.refresh(refreshToken);
          store.dispatch(updateToken(data.data));
          return data.data.accessToken;
        } catch (e: any) {
          console.error("[API] refresh failed", e);

          const status = e?.response?.status;
          const errorCodes: number[] | undefined = e?.response?.data?.error_codes;
          const isExpiredSpaRefresh = Array.isArray(errorCodes) && errorCodes.includes(700084);
          const isInvalidGrant = e?.response?.data?.error === "invalid_grant" || isExpiredSpaRefresh;

          // If backend explicitly returned 401/403 or invalid_grant -> try MSAL connect fallback
          if (status === 401 || status === 403 || isInvalidGrant) {
            try {
              // If MSAL says refresh token expired for SPA -> force interactive login
              if (isExpiredSpaRefresh) {
                // cleanup and redirect to interactive login
                // store.dispatch(clearAuth()); // uncomment if you have clearAuth
                try {
                  msalInstance.loginRedirect(loginRequest);
                } catch (msalErr) {
                  console.error("msal loginRedirect failed", msalErr);
                }
                throw e;
              }

              // Try to obtain MSAL token silently
              const accounts = msalInstance.getAllAccounts();
              if (!accounts?.length) {
                // no MSAL account -> interactive login required
                try {
                  msalInstance.loginRedirect(loginRequest);
                } catch (msalErr) {
                  console.error("msal loginRedirect failed", msalErr);
                }
                throw e;
              }

              let msalRes;
              try {
                msalRes = await msalInstance.acquireTokenSilent({
                  ...(loginRequest as any),
                  account: accounts[0],
                });
              } catch (silentErr: any) {
                const isInteractionRequired =
                  silentErr instanceof InteractionRequiredAuthError ||
                  silentErr?.errorCode === "interaction_required";
                if (isInteractionRequired) {
                  try {
                    msalInstance.acquireTokenRedirect(loginRequest);
                  } catch (msalErr) {
                    console.error("msal acquireTokenRedirect failed", msalErr);
                  }
                  throw silentErr;
                }
                throw silentErr;
              }

              const azureToken = msalRes?.accessToken || msalRes?.idToken;
              if (!azureToken) throw new Error("No MSAL token obtained for connect");

              // call backend connect with fresh MSAL token to obtain backend tokens
              const { data: connectResp } = await api.auth.connect(azureToken);
              // adjust below depending on your response shape:
              // many endpoints return { data: { accessToken, refreshToken, ... } }
              const backendTokens = connectResp?.data ?? connectResp; // try both shapes
              store.dispatch(updateToken(backendTokens));
              return backendTokens.accessToken;
            } catch (innerErr) {
              console.error("[API] refresh -> connect fallback failed", innerErr);
              throw innerErr;
            }
          }

          throw e;
        } finally {
          setTimeout(() => {
            refreshPromise = null;
          }, 0);
        }
      })();
    }

    try {
      const newAccessToken = await refreshPromise;
      originalConfig._retry = true;
      originalConfig.headers = originalConfig.headers || {};
      originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
      return serverInstance(originalConfig);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // 403 navigation
  if (response.status === 403) {
    if (
      response.config.method === "get" &&
      history.location.pathname !== ROUTES.FORBIDDEN
    ) {
      history.push(
        `${ROUTES.FORBIDDEN}?redirectedFrom=${history.location.pathname}`
      );
    }
  }

  if (response.status >= 400) {
    return Promise.reject(response);
  }

  return Promise.reject(httpResponse);
};
// ----- Attach interceptors -----
serverInstance.interceptors.request.use(requestInterceptor);
serverInstance.interceptors.response.use((res) => res, responseErrorInterceptor);
serverInstance.interceptors.response.use(undefined, httpError.handleHttpError);

// ----- API exports -----
const api = {
  auth: new Auth(serverInstance),
  permissions: new Permissions(serverInstance),
  profile: new Profile(serverInstance),
  roles: new Roles(serverInstance),
  sites: new Sites(serverInstance),
  poses: new Poses(serverInstance),
  services: new Services(serverInstance),
  search: new Search(serverInstance),
  users: new Users(serverInstance),
  groups: new Groups(serverInstance),
  paymentServices: new PaymentServices(serverInstance),
  dashboardSettings: new DashboardSettings(serverInstance),
  incidents: new Incidents(serverInstance),
  businessOperationsJournal: new JournalBusinessOperations(serverInstance),
  userActivityJournal: new UserActivityJournal(serverInstance),
  systemSettings: new SystemSettings(serverInstance),
  eventsLogJournal: new EventsLogJournal(serverInstance),
  systemLogJournal: new SystemLogJournal(serverInstance),
  metrics: new Metrics(serverInstance),
  dashboardCatalogs: new DashboardCatalogs(serverInstance),
  dashboardMetrics: new DashboardMetrics(serverInstance),
  servicesCategories: new ServicesCategories(serverInstance),
  bundles: new Bundles(serverInstance),
  serviceTemplates: new ServiceTemplates(serverInstance),
  evports: new EvPorts(serverInstance),
  zReportsJournal: new ZReportsJournal(serverInstance),
  goods: new Goods(serverInstance),
  keyManagement: new KeyManagement(serverInstance),
  idPrefix: new idPrefix(serverInstance),
};

export default api;